/**
 * JsonColumnsSection — renders the JSON-columns list inside the MetaInfo
 * manager (per-database dialog) and the global Settings → Meta Info view.
 * Each declaration is a single row showing the qualified column name and a
 * muted summary line about the structure-cache state (path count, sampled
 * rows, sampled-at relative time). A "Details" button opens a sub-dialog
 * with the inferred JsonShapePreview when cached paths exist.
 */

import { useMemo, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatRelativeTime } from '../../utils/formatRelativeTime';
import { JsonStructureDetailsDialog } from './JsonStructureDetailsDialog';
import { ProvenanceBadge } from './ProvenanceBadge';
import type {
  JsonColumnDeclaration,
  JsonStructureCache,
} from '@/types/electron';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';

export type JsonColumnsSectionProps = {
  declarations: JsonColumnDeclaration[];
  structureCache: JsonStructureCache[];
  onRemove: (decl: JsonColumnDeclaration) => void;
  /** Refetch context — when present, the "Details" dialog shows refresh /
   * filter affordances that re-sample the column from the database. */
  connectionId?: string;
  database?: string;
  engine?: DatabaseEngineId;
};

export function JsonColumnsSection({
  declarations,
  structureCache,
  onRemove,
  connectionId,
  database,
  engine,
}: JsonColumnsSectionProps) {
  const [detailsForId, setDetailsForId] = useState<string | null>(null);

  const cacheByKey = useMemo(() => {
    const map = new Map<string, JsonStructureCache>();
    for (const e of structureCache) {
      map.set(`${e.schema}.${e.table}.${e.column}`, e);
    }
    return map;
  }, [structureCache]);

  if (declarations.length === 0) {
    return (
      <div className="text-xs text-muted-foreground italic">
        No JSON columns declared yet.
      </div>
    );
  }

  const detailsDecl = detailsForId
    ? declarations.find((d) => d.id === detailsForId)
    : null;
  const detailsCache = detailsDecl
    ? cacheByKey.get(
        `${detailsDecl.schema}.${detailsDecl.table}.${detailsDecl.column}`,
      )
    : undefined;
  const hasRefetchContext = Boolean(connectionId && database && engine);

  return (
    <>
      {declarations.map((d) => {
        const cache = cacheByKey.get(`${d.schema}.${d.table}.${d.column}`);
        const hasPaths = !!cache && cache.paths.length > 0;

        return (
          <div
            key={d.id}
            className="flex flex-col gap-1 border rounded p-2"
            data-testid={`metainfo-json-row-${d.id}`}
          >
            <div className="flex items-center justify-between gap-2 text-xs">
              <span translate="no" className="font-mono truncate min-w-0">
                {d.schema}.{d.table}.{d.column}
              </span>
              <div className="flex items-center gap-1 shrink-0">
                <ProvenanceBadge provenance={d.provenance} />
                {(hasPaths || hasRefetchContext) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setDetailsForId(d.id)}
                    className="h-6 px-2 text-[11px]"
                    data-testid={`metainfo-json-details-${d.id}`}
                  >
                    Details
                  </Button>
                )}
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => onRemove(d)}
                  aria-label="Remove JSON-column declaration"
                  title="Remove JSON-column declaration"
                  className="h-6 w-6 text-destructive"
                  data-testid={`metainfo-json-remove-${d.id}`}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>

            <div
              className="text-[10px] text-muted-foreground"
              data-testid={`metainfo-json-cache-summary-${d.id}`}
            >
              {cache ? (
                <span>
                  {cache.paths.length} paths ·{' '}
                  {cache.sampledRows.toLocaleString()} rows · scanned{' '}
                  {formatRelativeTime(cache.sampledAt)}
                </span>
              ) : (
                <span className="italic">No paths cached yet</span>
              )}
            </div>
          </div>
        );
      })}
      {detailsDecl && (
        <JsonStructureDetailsDialog
          open
          onOpenChange={(o) => {
            if (!o) setDetailsForId(null);
          }}
          schema={detailsDecl.schema}
          table={detailsDecl.table}
          column={detailsDecl.column}
          cache={detailsCache ?? null}
          connectionId={connectionId}
          database={database}
          engine={engine}
        />
      )}
    </>
  );
}
